import { useEffect, useState } from "react";
import { BellRing, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, currentPermission, isSubscribed, enablePush } from "@/lib/push";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

export default function PushPromptBanner() {
  const [mode, setMode] = useState(null); // null | ios-install | enable | denied
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("push-banner-hidden") === "1");

  const check = async () => {
    if (isIOS() && !isStandalone()) return setMode("ios-install");
    if (!pushSupported()) return setMode(null);
    const p = currentPermission();
    if (p === "denied") return setMode("denied");
    const subbed = await isSubscribed();
    setMode(subbed && p === "granted" ? null : "enable");
  };

  useEffect(() => { check(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Notifiche attivate");
      await check();
    } catch (e) {
      toast.error(e?.message || "Errore attivazione notifiche");
      await check();
    } finally {
      setBusy(false);
    }
  };

  const hide = () => { sessionStorage.setItem("push-banner-hidden", "1"); setHidden(true); };

  if (!mode || hidden) return null;

  const text = {
    "ios-install": "Su iPhone: tocca Condividi e poi \"Aggiungi alla schermata Home\", apri l'app dall'icona e attiva le notifiche.",
    enable: "Attiva le notifiche per ricevere subito sul telefono i nuovi lavori e le modifiche.",
    denied: "Le notifiche sono bloccate. Sbloccale dalle impostazioni del browser/telefono per questo sito.",
  }[mode];

  return (
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900" data-testid="push-prompt-banner">
      <BellRing className="w-6 h-6 shrink-0 text-amber-600" />
      <p className="flex-1 text-sm font-medium" data-testid="push-prompt-text">{text}</p>
      <div className="flex items-center gap-2">
        {mode === "enable" && (
          <button onClick={enable} disabled={busy} data-testid="push-prompt-enable-button"
            className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 transition-colors">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Attiva notifiche
          </button>
        )}
        <button onClick={hide} aria-label="Chiudi" data-testid="push-prompt-close-button"
          className="rounded-lg p-2 text-amber-700 hover:bg-amber-100 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
